import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BaseUpsertComponent } from '@cl/core/components';
import { AuthService } from '@cl/core/services';
import { WorkTimesService } from '@cl/core/services/work-times.service';
import { DATE_TIME_FORMAT_WITH_SECONDS } from '@cl/shared/constants';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { EntityType } from '@cl/shared/models';
import { WorkTime } from '@cl/shared/models/work-times/work-time.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef } from 'ng-zorro-antd/modal';

@Component({
    selector: 'cl-upsert-work-time',
    templateUrl: './upsert-work-time.component.html',
    styleUrl: './upsert-work-time.component.less',
    standalone: false,
})
export class UpsertWorkTimeComponent extends BaseUpsertComponent<WorkTime, WorkTimesService> {
    GroupPermission = GroupPermissionsType;
    EntityType = EntityType;

    dateTimeFormatWithSeconds = DATE_TIME_FORMAT_WITH_SECONDS;

    stopTimerMode: boolean = false;

    get notesRequiredOnEdit(): boolean {
        return (
            this.stopTimerMode || !!this.originalObject.endDateTimeUtc || !this.authService.userHasPermission([GroupPermissionsType.WORK_TIME_ADMIN])
        );
    }

    constructor(
        protected workTimesService: WorkTimesService,
        protected messageService: NzMessageService,
        private modal: NzModalRef,
        private fb: UntypedFormBuilder,
        private authService: AuthService,
    ) {
        super(workTimesService, messageService);

        // set modal texts
        this.saveSuccessText = 'Laika uzskaites ieraksts saglabāts!';
        this.createObjectSaveErrorText = 'Kļūda pievienojot laika uzskaites ierakstu';
        this.updateObjectSaveErrorText = 'Kļūda labojot laika uzskaites ierakstu';
    }

    createForm(): UntypedFormGroup {
        return this.fb.group({});
    }

    createAndFillForm(): UntypedFormGroup {
        if (!this.stopTimerMode && this.authService.userHasPermission([GroupPermissionsType.WORK_TIME_ADMIN])) {
            return this.fb.group({
                startDateTimeUtc: [this.originalObject.startDateTimeUtc, [Validators.required]],
                endDateTimeUtc: [{ value: this.originalObject.endDateTimeUtc, disabled: !this.originalObject.endDateTimeUtc }, [Validators.required]],
                notes: [this.originalObject.notes, [Validators.maxLength(128), Validators.minLength(5)]],
            });
        }

        return this.fb.group({
            notes: [this.originalObject.notes, [Validators.required, Validators.maxLength(128), Validators.minLength(5)]],
        });
    }

    afterInit(): void {
        if (!this.isEditMode) {
            return;
        }

        const notesFormControl = this.objectForm.get('notes');
        if (!notesFormControl) {
            return;
        }

        if (this.notesRequiredOnEdit) {
            notesFormControl.addValidators(Validators.required);
        }
    }

    submitAndReturn(val: boolean | WorkTime): void {
        this.modal.close(typeof val === 'object' ? (val as WorkTime) : val);
    }

    cancel(): void {
        this.modal.close();
    }

    async save(): Promise<void> {
        if (this.isEditMode) {
            this.originalObject.startDateTimeUtc = new Date(this.originalObject.startDateTimeUtc).toISOString();
            if (this.originalObject.endDateTimeUtc) this.originalObject.endDateTimeUtc = new Date(this.originalObject.endDateTimeUtc).toISOString();

            await this.service.update(
                this.objectId,
                this.originalObject ? Object.assign(this.originalObject, this.objectForm.getRawValue()) : this.objectForm.getRawValue(),
            );
            return;
        }

        await this.service.create(this.objectForm.getRawValue());
    }
}
